import { consoleIfNotProd } from 'chatspot-core/loggers/consoleIfNotProd';
import { useCallback, useRef } from 'react';
import { MAX_DEPTH, getExtractedParameterNames } from './jsonSchemaUtils';
export const useFunctionQueue = () => {
  const queue = useRef([]);
  const isProcessing = useRef(false);
  const processQueue = useCallback(async () => {
    if (isProcessing.current || queue.current.length === 0) return;
    isProcessing.current = true;
    const {
      function: fn,
      functionParameters,
      name,
      asyncTimeout = 2000
    } = queue.current[0];
    try {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Function ${name} execution timed out after ${asyncTimeout}ms`));
        }, asyncTimeout);
        Promise.resolve(fn(functionParameters)).then(response => {
          clearTimeout(timeout);
          resolve(String(response));
        }).catch(reject);
      });
    } catch (error) {
      consoleIfNotProd.warn(error);
    }
    queue.current.shift();
    isProcessing.current = false;
    void processQueue();
  }, []);
  const addToQueue = useCallback(({
    function: fn,
    functionParameters,
    name,
    asyncTimeout = 2000
  }) => {
    queue.current.push({
      function: fn,
      functionParameters,
      name,
      asyncTimeout
    });
    void processQueue();
  }, [processQueue]);
  return {
    addToQueue
  };
};

// Used with v2.  Everything above should be deleted.
export const formatContextInstructions = actions => {
  const actionsAsMarkDown = actions.map(action => {
    const actionName = `## ${action.name}`;
    const formatValue = (keyPath, value, depth) => {
      if (depth >= MAX_DEPTH) {
        return [`current ${keyPath}: ${JSON.stringify(value)}`];
      }
      if (Array.isArray(value)) {
        // Iterate through array items with index notation
        return value.flatMap((item, index) => formatValue(`${keyPath}[${index}]`, item, depth + 1));
      }
      if (value !== null && typeof value === 'object') {
        // Recursively handle object properties
        return Object.entries(value).flatMap(([childKey, childVal]) => formatValue(`${keyPath}.${childKey}`, childVal, depth + 1));
      }

      // Primitive value or null
      return [`current ${keyPath}: ${JSON.stringify(value)}`];
    };
    const parameterLines = [];
    Object.entries(action.functionParameters).forEach(([paramName, param]) => {
      parameterLines.push(...formatValue(paramName, param.currentValue, 0));
    });
    const parameters = parameterLines.join('\n');
    return parameters ? `${actionName}\n${parameters}` : actionName;
  }).join('\n');
  return `# You may take actions\n${actionsAsMarkDown}`;
};

// // TODO: figure out character limit for external team - and throw error if too long.
export const formatResponseInstructions = additionalLLMInstructions => `# Response Guidelines
1. Communication
- Respond in clear, friendly, and concise natural language. Avoid technical jargon unless explicitly asked.
- Present multiple options or alternatives as HTML ordered or unordered lists when appropriate

2. Actions
- When the user makes a direct change request (e.g., “update the executive summary”), apply the change and return the updated result
- Infer reasonable defaults from context; only ask follow-up questions if absolutely necessary to prevent incorrect behavior
- If the requested action cannot be completed, briefly explain why and offer alternative suggestions
- Use human-readable values in function parameters (e.g., names instead of IDs)

3. Button Behavior
- Set **showAsButton** to true for suggestions, improvements, or unclear commands that need confirmation
- Set **showAsButton** to false for direct commands where the action can be performed immediately
- If **showAsButton** is being set to true, the buttonDescription should a description of the action that will be performed if the user clicks the button and it should give the user a good understanding of what will be done.
- If **showAsButton** is being set to false, the buttonDescription should be a description of the action that was performed and it should give the user a good understanding of what was done.
- Always include buttonText, buttonExplanation, and buttoHeader, even when showAsButton is false.

${additionalLLMInstructions ? `## Page Instructions\n${additionalLLMInstructions}` : ''}
`;

// THIS WILL BE REMOVED ONCE https://git.hubteam.com/HubSpot/chatspot-ui/pull/3095 is merged
export const getCurrentPageInteractions = (...___fake) => {
  return {
    pageName: 'TEMPORARY IN ORDER NOT TO BREAK THE BUILD',
    interactions: []
  };
};

// Data for comparing response with state
export const getMatchingDefinedFunction = (customFEActions, functionName) => {
  if (!customFEActions) {
    return null;
  }
  const currentValueOfProperty = customFEActions.find(interactionState => interactionState.name === functionName);
  if (!currentValueOfProperty) {
    return null;
  }
  return currentValueOfProperty;
};
export const getCurrentStateOfFunctionValuesByName = (customFEActions, functionName) => {
  const currentValueOfProperty = getMatchingDefinedFunction(customFEActions, functionName);
  if (!currentValueOfProperty) {
    return null;
  }
  const parameterState = Object.entries(currentValueOfProperty.functionParameters).reduce((acc, [key, value]) => {
    acc[key] = value.currentValue;
    return acc;
  }, {});
  return parameterState;
};
const deepCompareValues = (current, llm) => {
  if (current === llm) return true;
  if (current == null || llm == null) return current === llm;

  // Handle arrays
  if (Array.isArray(current) && Array.isArray(llm)) {
    if (current.length !== llm.length) return false;
    return current.every((item, index) => deepCompareValues(item, llm[index]));
  }

  // Handle objects
  if (typeof current === 'object' && typeof llm === 'object') {
    const llmKeys = Object.keys(llm);
    // Only compare the keys that exist in the LLM parameters
    return llmKeys.every(key => deepCompareValues(current[key], llm[key]));
  }

  // Handle primitive values (convert to string for comparison to handle type differences)
  return String(current) === String(llm);
};
export const getAreAllParametersTheSame = (currentValuesOfFunction, llmDeterminedAction) => {
  const llmDefinedParameters = getExtractedParameterNames(llmDeterminedAction);
  return Object.keys(llmDefinedParameters).every(key => {
    const currentValue = currentValuesOfFunction[key];
    const llmValue = llmDefinedParameters[key];
    const isEqual = deepCompareValues(currentValue, llmValue);
    return isEqual;
  });
};
export const getUndoAction = (currentValuesOfFunction, action) => {
  return Object.assign({}, action, currentValuesOfFunction);
};
export const getShouldShowAsButton = (llmDeterminedActions, userDefinedActions) => {
  if (!llmDeterminedActions || !userDefinedActions) {
    return false;
  }
  const matchingFunction = llmDeterminedActions.map(action => {
    return getMatchingDefinedFunction(userDefinedActions, action.functionName);
  });
  if (!matchingFunction) {
    return false;
  }
  const areAnyShowAsButton = llmDeterminedActions === null || llmDeterminedActions === void 0 ? void 0 : llmDeterminedActions.some(action => action.showAsButton);
  const doAnyRequireConfirmation = matchingFunction.some(action => action === null || action === void 0 ? void 0 : action.requiresUserConfirmation);
  return areAnyShowAsButton || doAnyRequireConfirmation;
};